import {Component, OnInit} from '@angular/core';
import {AlertTypesEnum} from '../shared/interfaces/AlertTypesEnum';
import {getDateFromISO} from '../shared/helpers/helpers';
import {Select, Store} from '@ngxs/store';
import {Observable} from 'rxjs/internal/Observable';
import {ITeamRecord} from './interfaces/team-record.interface';
import {ProfileState} from '../shared/states/profile/profile.state';
import {
  DeleteUser,
  GetUserTeamList,
  ResendUserInvitation,
  RevokeUserInvitation,
  SendInvitation,
  SetUserTeamList,
} from '../shared/states/profile/profile.actions';
import {UserRolesEnum} from '../shared/interfaces/UserRolesEnum';
import {BadgeColorsEnum} from '../shared/interfaces/BadgeColorsEnum';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {take, withLatestFrom} from 'rxjs/operators';
import {IInvitation} from '../shared/interfaces/invitation.interface';
import {BaseTableDirective} from '../shared/directives/base-table.directive';
import {IAlertMessage} from '../shared/interfaces/alert-message.interface';
import {AuthState} from '../shared/states/auth/auth.state';
import {UserStatusEnum} from '../shared/interfaces/UserStatusEnum';
import {IUser} from '../shared/interfaces/user.interface';
import { PharmacyState } from '../shared/states/pharmacy/pharmacy.state';
import { GetPharmacyById } from '../shared/states/pharmacy/pharmacy.actions';
import { TeamRoleEnum } from '../shared/interfaces/TeamRoleEnum';
import { environment } from 'src/environments/environment';
@UntilDestroy()
@Component({
  selector: 'app-team',
  templateUrl: './team.component.html',
  styleUrls: ['./team.component.scss'],
})
export class TeamComponent extends BaseTableDirective implements OnInit {
  showResend: boolean[] = [];

  AlertTypesEnum = AlertTypesEnum;

  showInviteDialog: boolean;
  showEditUserDialog: boolean;
  getDateFromISO = getDateFromISO;

  UserRolesEnum = UserRolesEnum;
  UserStatusEnum = UserStatusEnum;
  BadgeColorsEnum = BadgeColorsEnum;
  TeamRoleEnum = TeamRoleEnum;

  tableColumns = {
    userName: true,
    role: true,
    email: true,
    createDate: true,
    recordsCount: true,
    status: true,
  };

  teamList: ITeamRecord[] = [];

  showMessage: boolean;
  alertMessage: IAlertMessage;
  autohideDelay = 3;

  chosenUser: ITeamRecord;

  showRevokeDialog: boolean;
  showDeleteDialog: boolean;
  inviteResent: boolean;

  @Select(ProfileState.success) success$: Observable<boolean>;
  @Select(ProfileState.loading) loading$: Observable<boolean>;
  @Select(ProfileState.teamList) teamList$: Observable<ITeamRecord[]>;
  @Select(AuthState.currentUser) currentUser$: Observable<IUser>;
  @Select(PharmacyState.currentPharmacy) currentPharmacy$: Observable<any>;

  isDevMode = !environment.production;

  constructor(
    protected store: Store
  ) {
    super(store);
  }

  ngOnInit(): void {
    this.store.dispatch(new GetUserTeamList());
    this.teamList$.pipe(untilDestroyed(this))
      .subscribe((teamList: ITeamRecord[]) => {
        this.teamList = teamList;
        this.showResend = (teamList && Array(teamList.length).fill(true)) || [];
      });
    this.currentPharmacy$.pipe(untilDestroyed(this))
      .subscribe((data: any) => {
        if (data?.id) {
          this.store.dispatch(new GetUserTeamList());
        }
      });
  }

  openInviteDialog(): void {
    this.showInviteDialog = true;
  }

  openEditDialog(user: ITeamRecord): void {
    this.chosenUser = user;
    this.showEditUserDialog = true;
  }

  afterInvite(): void {
    this.showMessage = true;

    this.alertMessage = {
      type: AlertTypesEnum.Success,
      text: 'Invite successfully sent.',
    };

    this.store.dispatch(new GetUserTeamList());
    this.closeInviteDialog();
  }

  closeInviteDialog(): void {
    this.showInviteDialog = false;
  }

  afterSaved(success: boolean): void {
    this.closeEditDialog();
    this.showMessage = true;
    this.alertMessage = {
      type: success ? AlertTypesEnum.Success : AlertTypesEnum.Error,
      text: success ? 'Changes saved successfully.' : 'Failed to.',
    };

    if (success) {
      this.store.dispatch(new GetUserTeamList());
    }
  }

  closeEditDialog(): void {
    this.showEditUserDialog = false;
  }

  openRevokeDialog(user: ITeamRecord): void {
    this.chosenUser = user;
    this.showRevokeDialog = true;
  }

  closeRevokeDialog(): void {
    this.showRevokeDialog = false;
  }

  openDeleteDialog(user: ITeamRecord): void {
    this.chosenUser = user;
    this.showDeleteDialog = true;
  }

  closeDeleteDialog(): void {
    this.showDeleteDialog = false;
  }

  resend(id: string): void {
    this.store.dispatch(new ResendUserInvitation(id))
      .pipe(take(1), withLatestFrom(this.success$))
      .subscribe(([_, success]) => {
        this.closeRevokeDialog();
        this.showMessage = true;
        this.alertMessage = {
          type: success ? AlertTypesEnum.Success : AlertTypesEnum.Error,
          text: success ? 'Invite successfully resent.' : 'Resend failed.',
        };

        if (success) {
          this.inviteResent = true;
          this.store.dispatch(new GetUserTeamList());
          setTimeout(() => this.inviteResent = false, 300);
        }
      });
  }

  revoke(): void {
    this.store.dispatch(new RevokeUserInvitation(this.chosenUser.email))
      .pipe(take(1), withLatestFrom(this.success$))
      .subscribe(([_, success]) => {
        this.closeRevokeDialog();
        this.showMessage = true;
        this.alertMessage = {
          type: success ? AlertTypesEnum.Success : AlertTypesEnum.Error,
          text: success ? 'Invite successfully revoked.' : 'Failed to revoke invite.',
        };

        if (success) {
          this.store.dispatch(new GetUserTeamList());
        }
      });
  }

  delete(): void {
    this.store.dispatch(new DeleteUser(this.chosenUser.email))
      .pipe(take(1), withLatestFrom(this.success$))
      .subscribe(([_, success]) => {
        this.closeDeleteDialog();
        this.showMessage = true;
        this.alertMessage = {
          type: success ? AlertTypesEnum.Success : AlertTypesEnum.Error,
          text: success ? 'User successfully deleted.' : 'Failed to delete user.',
        };

        if (success) {
          this.store.dispatch(new GetUserTeamList());
        }
      });
  }

  sortColumn(column: string): void {
    this.store.dispatch(new SetUserTeamList(this.sort(this.teamList, column)));
  }

  isProfileOwner(email: string): boolean {
    return email === this.store.selectSnapshot(AuthState.currentUser).email;
  }
}
